import type { IOrderItem } from "@/data/model/Order";
import { Format } from "@/data/util/Format";

import { Component } from "@/shared/elements/Component";
import { renderItemIconsHtml } from "@/shared/helpers/AttributeHelper";
import { DiscountUtil } from "@/shared/utils/DiscountUtil";
import { toMoney } from "@/shared/utils/format";

import { getNameInLocationLanguage } from "@/shared/helpers/ItemNameHelper";
import { OptionGroup } from "@/operator/elements/OptionGroup";

export class OptionGroupOption extends Component {
    public readonly orderItem: IOrderItem;
    public readonly isParentItemCancelled: boolean;
    public readonly displayPrice: boolean;

    constructor(option: IOrderItem, isParentItemCancelled: boolean, displayPrice: boolean = false) {
        super();
        this.orderItem = option;
        this.isParentItemCancelled = isParentItemCancelled;
        this.displayPrice = displayPrice;

        this.$element = this.drawElement();
    }

    protected drawElement() {
        const orderItem = this.orderItem;

        const $element = $('<div class="item" />');
        const $optionHead = $('<div class="head" />').appendTo($element);
        const $optionName = $('<div class="name" />').appendTo($optionHead);

        if (orderItem.quantity !== 1) $optionName.append(`<div class="quantity">${orderItem.quantity}x</div>`);

        $optionName.append((this.getDisplayName() ?? "") + renderItemIconsHtml(orderItem.articleId));

        if (this.displayPrice) {
            if (this.orderItem.hasDiscount && !this.orderItem.isCancelled && this.orderItem.unitPrice != 0) {
                const icon = DiscountUtil.getDiscountIcon(this.orderItem.lastDiscountType);

                $optionHead.append(`<div class="price has-discount">
                    <div class="price-before-discount" style="color: #fd397a; text-decoration: line-through;">
                        <span class="discount-type">
                            <i class="${icon}"></i>
                        </span>
                        ${this.getDisplayPrice()}
                    </div>
                    <div class="price-after-discount">
                        ${toMoney(this.orderItem.currentTotalAmountAfterDiscount, true)}
                    </div>
                </div>`);
            } else {
                $optionHead.append(`<div class="price">${this.getDisplayPrice()}</div>`);
            }
        }

        // draw option groups
        if (orderItem.options.length) {
            const $subContainer = $('<div class="subs" />').appendTo($element);
            for (const optionGroup of orderItem.options) {
                OptionGroup.render($subContainer, optionGroup, this.isParentItemCancelled, this.displayPrice);
            }
        }

        return $element;
    }

    private getDisplayName(): string | undefined {
        return Format.htmlEscape(getNameInLocationLanguage(this.orderItem, true));
    }

    private getDisplayPrice() {
        if (this.orderItem.unitPrice === 0) return "";

        return toMoney(
            this.orderItem.isCancelled ? this.orderItem.originalTotalAmount : this.orderItem.currentTotalAmount,
            true
        );
    }

    public static render(
        $appendToTarget: JQuery,
        orderItem: IOrderItem,
        isParentItemCancelled: boolean,
        displayPrice: boolean = false
    ): OptionGroupOption {
        const component = new OptionGroupOption(orderItem, isParentItemCancelled, displayPrice);
        component.renderTo($appendToTarget);

        return component;
    }
}
